/**
 * SESSION SERVICE - Re-export from Shared Core
 * 
 * Mantiene compatibilidad con imports existentes.
 * La implementación real está en @boosting/core.
 */

import { db, auth } from '@/core/config/firebase';
import { Logger } from '@/core/utils';
import {
  SessionService as CoreSessionService,
  createSessionService,
  type ActiveSession,
  type DeviceInfo,
  type SessionServiceConfig,
} from '@boosting/core';
import { auditLogService } from './audit-log.service';

// Re-export types
export type { ActiveSession, DeviceInfo, SessionServiceConfig };

const logger = Logger.getLogger('SessionService');

// Create core service with Appmor's Firestore
const coreService = createSessionService(db as any, {
  collectionName: 'active_sessions',
  sessionStorageKey: 'appmor_session_id',
  maxSessions: 5,
  activityIntervalMs: 5 * 60 * 1000
});

/**
 * Extended Session Service for Appmor
 * Adds audit logging integration
 */
class AppmorSessionService {
  /**
   * Registrar nueva sesión al hacer login
   */
  async registerSession(userId: string): Promise<string> {
    const sessionId = await coreService.registerSession(userId);

    // Audit log
    await auditLogService.log({
      action: 'SESSION_STARTED',
      userId,
      resourceType: 'session',
      resourceId: sessionId,
      details: { deviceInfo: coreService.getDeviceInfo() }
    });

    return sessionId;
  }

  /**
   * Obtener sesiones activas del usuario
   */
  async getActiveSessions(userId: string): Promise<ActiveSession[]> {
    return coreService.getActiveSessions(userId);
  }

  /**
   * Cerrar una sesión específica
   */
  async revokeSession(sessionId: string, userId?: string): Promise<void> {
    await coreService.revokeSession(sessionId);

    if (userId) {
      await auditLogService.log({
        action: 'SESSION_REVOKED',
        userId,
        resourceType: 'session',
        resourceId: sessionId
      });
    }
  }

  /**
   * Cerrar TODAS las sesiones (logout global)
   */
  async revokeAllSessions(userId: string): Promise<number> {
    const count = await coreService.revokeAllSessions(userId);

    await auditLogService.log({
      action: 'ALL_SESSIONS_REVOKED',
      userId,
      resourceType: 'session',
      details: { count }
    });

    return count;
  }

  /**
   * Cerrar todas EXCEPTO la actual
   */
  async revokeOtherSessions(userId: string): Promise<number> {
    const sessions = await this.getActiveSessions(userId);
    const currentSessionId = localStorage.getItem('appmor_session_id');

    const sessionsToRevoke = sessions.filter(s => s.id !== currentSessionId);

    if (sessionsToRevoke.length === 0) {
      return 0;
    }

    const batch = db.batch();
    sessionsToRevoke.forEach((session: ActiveSession) => {
      batch.delete(db.collection('active_sessions').doc(session.id));
    });

    await batch.commit();

    logger.info('Other sessions revoked', { userId, count: sessionsToRevoke.length });

    await auditLogService.log({
      action: 'OTHER_SESSIONS_REVOKED',
      userId,
      resourceType: 'session',
      details: { count: sessionsToRevoke.length }
    });

    return sessionsToRevoke.length;
  }

  /**
   * Actualizar última actividad
   */
  async touchSession(): Promise<void> {
    return coreService.touchSession();
  }

  /**
   * Cerrar sesión actual (al hacer logout)
   */
  async endCurrentSession(): Promise<void> {
    const sessionId = localStorage.getItem('appmor_session_id');
    const userId = auth.currentUser?.uid;

    if (sessionId && userId) {
      await this.revokeSession(sessionId, userId);
    }

    localStorage.removeItem('appmor_session_id');
    coreService.stopActivityTracking();
  }

  /**
   * Verificar si hay una sesión activa válida
   */
  async isSessionValid(): Promise<boolean> {
    return coreService.isSessionValid();
  }

  /**
   * Start activity tracking
   */
  startActivityTracking(): void {
    coreService.startActivityTracking();
  }

  /**
   * Stop activity tracking
   */
  stopActivityTracking(): void {
    coreService.stopActivityTracking();
  }

  /**
   * Get device info
   */
  getDeviceInfo(): DeviceInfo {
    return coreService.getDeviceInfo();
  }
}

export const sessionService = new AppmorSessionService();
